<template>
  <div class="number-input">
    <button
      @click="decrease"
      @mouseenter="minusMouseenter = true"
      @mouseleave="minusMouseenter = false"
      :style="{ background: minusBg}"
      :class="{ disabled: modelValue <= min || disabledMinus }"
      class="round-button"
    >
      <SvgIcon
        :svg="MinusSvg"
        :color="minusColor"
      />
    </button>
    <input
      :value="modelValue"
      :disabled="disabledMinus && disabledPlus"
      @input="onInput"
      type="number"
    >
    <button
      @click="increase"
      @mouseenter="plusMouseenter = true"
      @mouseleave="plusMouseenter = false"
      :style="{ background: plusBg}"
      :class="{ disabled: disabledPlus }"
      class="round-button"
    >
      <SvgIcon
        :svg="PlusSvg"
        :color="plusColor"
      />
    </button>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import MinusSvg from '@/assets/images/minus.svg?raw'
import PlusSvg from '@/assets/images/plus.svg?raw'

const props = defineProps({
  disabledMinus: {
    type: Boolean,
    default: false
  },
  disabledPlus: {
    type: Boolean,
    default: false
  },
  min: {
    type: Number,
    default: 0
  },
  max: {
    type: Number,
    default: Infinity
  },
  step: {
    type: Number,
    default: 1
  }
})

const emit = defineEmits(['minus', 'plus', 'change'])

const modelValue = defineModel({ required: true })

const minusMouseenter = ref(false)
const plusMouseenter = ref(false)

const minusBg = computed(() => {
  if (modelValue.value <= props.min || props.disabledMinus) return '#ebebeb'
})

const plusBg = computed(() => {
  if (modelValue.value >= props.max || props.disabledPlus) return '#ebebeb'
})

const minusColor = computed(() => {
  if (props.disabledMinus) return '#B2B7C2'
  return minusMouseenter.value ? modelValue.value <= props.min ? '#B2B7C2' : '#ffffff' : ''
})

const plusColor = computed(() => {
  if (props.disabledPlus) return '#B2B7C2'
  return plusMouseenter.value ? '#ffffff' : ''
})

const increase = () => {
  if (props.disabledPlus) return
  if (modelValue.value + props.step <= props.max) {
    modelValue.value++
    emit('plus')
  }
}

const decrease = () => {
  if (props.disabledMinus) return
  if (modelValue.value - props.step >= props.min) {
    modelValue.value--
    emit('minus')
  }
}

const onInput = (e) => {
  if (props.disabledMinus && props.disabledPlus) return

  const val = Number(e.target.value)
  if (!isNaN(val)) {
    modelValue.value = val
    emit('change')
  }
}
</script>

<style scoped lang="less">
.number-input {
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1.5px solid var(--light-split-color);
  border-left: none;
  border-right: none;
  border-radius: 14px;
  height: 28px;
  background-color: #fff;
}

input {
  flex: 1;
  width: 100%;
  height: 100%;
  border: none;
  text-align: center;
  outline: none;
  font-size: 12px;
  color: #232323;
  font-family: mon-500;

  &:disabled {
    background-color: transparent;
    color: #b2b7c2;
  }
}

input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type=number] {
  -moz-appearance: textfield; /* Firefox */
}

.round-button {
  width: 28px;
  height: 28px;
  border: 1px solid var(--light-split-color);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  background-color: white;

  &.disabled {
    cursor: not-allowed;
  }
}

.round-button:hover {
  background-color: #052784;
}

</style>
